export const COMPANION_TABS = [
  "trip",
  "load",
  "weather",
  "map",
  "log",
  "dashboard",
  "brief",
  "duty",
  "checks",
  "messages",
] as const;

export type CompanionTab = (typeof COMPANION_TABS)[number];

/** Desk-aligned catalog. Pilot nav hides the sections a crew phone does not open. */
export const COMPANION_TAB_LABEL: Record<CompanionTab, string> = {
  trip: "Trip",
  load: "Load",
  weather: "Weather",
  map: "Map",
  log: "Log",
  dashboard: "Dashboard",
  brief: "Brief",
  duty: "Duty",
  checks: "Checks",
  messages: "Messages",
};

/**
 * Crew phone. Today’s assignment, brief, duty, checks, log, dispatch messages.
 * Load, map, weather-as-its-own-module, and dashboard stay in the catalog.
 */
export const PILOT_NAV_TABS = ["trip", "brief", "duty", "checks", "log", "messages"] as const;

/** Flight page. Five tabs, most-used first. Home stays outside this strip. */
export const FLIGHT_TABS = ["brief", "duty", "log", "pax", "checks"] as const;

export type FlightTab = (typeof FLIGHT_TABS)[number];

export const FLIGHT_TAB_LABEL: Record<FlightTab, string> = {
  brief: "Brief",
  duty: "Duty",
  log: "Log",
  pax: "Pax",
  checks: "Checklist",
};

export function isFlightTab(raw: unknown): raw is FlightTab {
  return FLIGHT_TABS.includes(raw as FlightTab);
}

export type PilotTab = (typeof PILOT_NAV_TABS)[number];

export const HIDDEN_NAV_TABS = ["load", "weather", "map", "dashboard"] as const;

export const PILOT_TAB_LABEL: Record<PilotTab, string> = {
  trip: "Today",
  brief: "Brief",
  duty: "Duty",
  checks: "Checks",
  log: "Log",
  messages: "Msgs",
};

export const PILOT_TAB_ARIA: Record<PilotTab, string> = {
  trip: "Today",
  brief: "Brief",
  duty: "Duty",
  checks: "Checks",
  log: "Log",
  messages: "Dispatch messages",
};

export function isCompanionTab(raw: unknown): raw is CompanionTab {
  return COMPANION_TABS.includes(raw as CompanionTab);
}

export function isPilotNavTab(raw: unknown): raw is PilotTab {
  return PILOT_NAV_TABS.includes(raw as PilotTab);
}

export function hydrateCompanionTab(raw?: unknown): CompanionTab {
  return isCompanionTab(raw) ? raw : "trip";
}

export function hydratePilotTab(raw?: unknown): PilotTab {
  return isPilotNavTab(raw) ? raw : "trip";
}

/** Phone map tab is a stub. The live chart is a later phase, so the shell does not pin a viewport. */
export function tabPinsMap(_tab: CompanionTab): boolean {
  return false;
}

/** Today is home. Any other tab pushes. Tapping Today clears the stack. */
export function pushPilotNav(stack: readonly PilotTab[], next: PilotTab): PilotTab[] {
  if (next === "trip") return ["trip"];
  if (stack[stack.length - 1] === next) return [...stack];
  const base = stack.length ? [...stack] : (["trip"] as PilotTab[]);
  return [...base, next];
}

export function popPilotNav(
  stack: readonly PilotTab[],
): { action: "exit" } | { action: "pop"; stack: PilotTab[]; tab: PilotTab } {
  if (stack.length <= 1) return { action: "exit" };
  const next = stack.slice(0, -1);
  return { action: "pop", stack: next, tab: next[next.length - 1] ?? "trip" };
}
